import type { ComponentChildren } from "preact";
import { cx } from "./lib/cx.ts";

export type CardTone = "cyan" | "emerald" | "violet" | "pink" | "slate";

export interface FeatureCardProps {
  tone?: CardTone;
  eyebrow?: string;
  title: string;
  /** Drawn in a soft circular disc at the right edge. Pass a 36px svg. */
  icon?: ComponentChildren;
  /** A decorative illustration above the head, with a neutral body below. See Design > UI, "Dashboard emphasis". */
  cover?: ComponentChildren;
  /** The small line under the title, such as "View items". */
  hint?: ComponentChildren;
  /** When set, the card head becomes a toggle button (`aria-pressed` follows `selected`). */
  onSelect?: () => void;
  /** Accessible name for the toggle, such as "Filter by Water". */
  selectLabel?: string;
  selected?: boolean;
  /** Desaturated and dimmed because another card is selected; restores on hover or focus. */
  subdued?: boolean;
  children?: ComponentChildren;
  /** Below a hairline at the bottom of the card, such as a few links. */
  footer?: ComponentChildren;
  class?: string;
}

/**
 * A gradient card for choosing among a handful of featured things. Rule: at
 * most four or five on screen, one tone each, ink always the dark tone ink
 * (never white text on the gradient). Selecting one rings it and subdues the
 * others rather than hiding them.
 */
export function FeatureCard({ tone = "slate", eyebrow, title, icon, cover, hint, onSelect, selectLabel, selected, subdued, children, footer, class: className }: FeatureCardProps) {
  const head = (
    <>
      {eyebrow && <span class="ui-card__eyebrow">{eyebrow}</span>}
      <span class="ui-card__title">{title}</span>
      {hint && <span class="ui-card__hint">{hint}</span>}
    </>
  );
  return (
    <article class={cx("ui-card", `ui-card--${tone}`, cover ? "ui-card--cover" : undefined, className)} data-state={selected ? "selected" : subdued ? "subdued" : "idle"}>
      {cover && <div class="ui-card__cover" aria-hidden="true">{cover}</div>}
      {onSelect ? (
        <button type="button" class="ui-card__head" aria-pressed={!!selected} aria-label={selectLabel} onClick={onSelect}>
          {head}
        </button>
      ) : (
        <div class="ui-card__head">{head}</div>
      )}
      {icon && (
        <div class="ui-card__icon" aria-hidden="true">
          {icon}
        </div>
      )}
      {children && <div class="ui-card__body">{children}</div>}
      {footer && <div class="ui-card__foot">{footer}</div>}
    </article>
  );
}
